import React, { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Pencil, Trash2, ArrowLeft } from "lucide-react";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import ErrorMessage from "../../components/ui/ErrorMessage";
import {
  useDeleteQuietPeriod,
  useQuietPeriod,
} from "../../hooks/useQuietPeriods";
import AuditInfo from "../../components/ui/AuditInfo";
import { auditApi } from "../../services/api";

const QuietPeriodDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const quietPeriodId = Number(id);
  const navigate = useNavigate();
  const { data: qp, isLoading, error, refetch } = useQuietPeriod(quietPeriodId);
  const deleteMutation = useDeleteQuietPeriod();
  const [confirming, setConfirming] = useState(false);

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(quietPeriodId);
      navigate("/quiet-periods");
    } catch (e) {}
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !qp) {
    return (
      <ErrorMessage
        message="Failed to load dynamic date. Please try again."
        onRetry={() => refetch()}
        className="py-12"
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{qp.name}</h2>
          <p className="text-sm text-gray-500">Dynamic date details</p>
        </div>
        <div className="space-x-2">
          <Link
            to={`/quiet-periods/${qp.id}/edit`}
            className="btn-secondary inline-flex items-center px-4 py-2 rounded-md"
          >
            <Pencil className="w-4 h-4 mr-2" /> Edit
          </Link>
          <button
            onClick={() => setConfirming(true)}
            className="btn-danger inline-flex items-center px-4 py-2 rounded-md"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Delete
          </button>
        </div>
      </div>

      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Overview
          </h3>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
          <dl className="sm:divide-y sm:divide-gray-200">
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Name</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {qp.name}
              </dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Description</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {qp.description || "—"}
              </dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">From</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {new Date(qp.from).toLocaleString()}
              </dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">To</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {new Date(qp.to).toLocaleString()}
              </dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Tags</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {qp.tags && qp.tags.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {qp.tags.map((t, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-700 border border-blue-200"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                ) : (
                  "—"
                )}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {confirming && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900">
              Delete dynamic date
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              Are you sure you want to delete this dynamic date? This action
              cannot be undone.
            </p>
            <div className="mt-4 flex justify-end space-x-2">
              <button
                className="btn-secondary px-4 py-2 rounded-md"
                onClick={() => setConfirming(false)}
              >
                Cancel
              </button>
              <button
                className="btn-danger px-4 py-2 rounded-md"
                onClick={handleDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <button
        onClick={() => navigate(-1)}
        className="btn-secondary inline-flex items-center px-4 py-2 rounded-md"
      >
        <ArrowLeft className="w-4 h-4 mr-2" /> Back
      </button>

      {quietPeriodId ? (
        <AuditInfo
          resource="quiet-periods"
          id={quietPeriodId}
          fetch={auditApi.getLastChange}
        />
      ) : null}
    </div>
  );
};

export default QuietPeriodDetailPage;
